import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computed } from 'mobx';
import { computedFn } from 'mobx-utils/lib/computedFn';
import type { ClientItemId } from 'owa-client-ids';
import { constructKeytip, getKeytipString } from 'owa-command-ribbon';
import { getDefaultSlrAppRibbonButtonStyles } from 'owa-command-ribbon-styles';
import { getDefaultRibbonStyles } from 'owa-command-ribbon-styles/lib/util/getDefaultRibbonStyles';
import { getControlInfo } from 'owa-mail-ribbon-utils';
import { MailRibbonControlId, MailRibbonTabId } from 'owa-ribbon-ids';
import {
    getAssignPolicySections,
    getChangeNoteColorSections,
    getFlagUnflagSections,
    getForwardSections,
    getJunkSections,
    getRulesSections,
    getQuickStepsSections,
} from '../sections';
import {
    onDeleteNoteClicked,
    onViewEmailFromNoteClicked,
    onDeleteClicked,
    onFlagUnflagClicked,
    onForwardClicked,
    onMarkAsReadUnreadClicked,
    onReplyAllClicked,
    onReplyClicked,
    onPrintClicked,
    onReplyWithMeetingClicked,
    onJunkNotJunkClicked,
    onMarkAsPhishingClicked,
    onShowInImmersiveReaderClicked,
    onPMEClicked,
} from '../../util/commandActions';
import {
    createMailAppButton,
    createMailAppFlyoutAnchor,
    createMailAppSplitButton,
} from '../../util/createMailRibbonButton';
import { getReadRibbonId } from '../../util/getReadRibbonId';
import { getToolbarRibbonIconColor } from '../../util/getToolbarRibbonIconColor';
import { getIsPopoutButtonDisabled } from '../../util/getIsPopoutButtonDisabled';
import { getQuickSteps } from 'owa-mail-quick-steps-store/lib/util/getQuickSteps';
import { onExecuteQuickStep } from '../../util/onQuickStepCommandAction';
import { getIsQuickStepDisabled } from '../../util/getIsQuickStepDisabled';
import { getIconNameFromIconId } from 'owa-mail-quick-steps-store/lib/util/getQuickStepIcon';

/**
 * This file is for shared components in read-only scenarios. Meaning, any tab or control that only shows up
 * when a user is composing. In this case, Home tab, View tab, and Notes tab are good examples.
 *
 * For controls that show up in compose-only scenarios, please use composeSharedControlDefinitions.ts.
 */

const computeDeleteNoteControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Notes_DeleteNote);
        return createMailAppButton(
            MailRibbonControlId.Notes_DeleteNote,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onDeleteNoteClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Notes)],
                getKeytipString(MailRibbonControlId.Notes_DeleteNote)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    }
);
export const getDeleteNoteControl = () => computeDeleteNoteControl.get();

const computeChangeNoteColorControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Notes_ChangeColor);
        return createMailAppFlyoutAnchor(
            MailRibbonControlId.Notes_ChangeColor,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            {
                sections: [
                    {
                        controls: getChangeNoteColorSections(),
                    },
                ],
            },
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Notes)],
                getKeytipString(MailRibbonControlId.Notes_ChangeColor),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    }
);
export const getChangeNoteColorControl = () => computeChangeNoteColorControl.get();

const computeNotesViewEmailControl = computed(
    (): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Notes_ViewEmail);
        return createMailAppButton(
            MailRibbonControlId.Notes_ViewEmail,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onViewEmailFromNoteClicked,
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Notes)],
                getKeytipString(MailRibbonControlId.Notes_ViewEmail)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            getDefaultRibbonStyles()
        );
    }
);
export const getNotesViewEmailControl = () => computeNotesViewEmailControl.get();

// Controls used by all of the read tabs

export const getDeleteControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.DeleteItem);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.DeleteItem, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onDeleteClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.DeleteItem),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(MailRibbonControlId.DeleteItem, itemId),
                  }
                : undefined
        );
    }
);

export const getPhishingControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Phishing);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.Phishing, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onMarkAsPhishingClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.Phishing)
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.DeleteItem, itemId) }
        );
    }
);

export const getJunkControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.JunkFlyout);
        return createMailAppSplitButton(
            getReadRibbonId(MailRibbonControlId.JunkFlyout, itemId),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            {
                sections: [
                    {
                        controls: getJunkSections(keytipTabId, undefined, itemId, targetWindow),
                    },
                ],
            },
            onJunkNotJunkClicked(itemId),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.JunkFlyout),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(MailRibbonControlId.JunkFlyout, itemId),
                  }
                : undefined
        );
    }
);

export const getRulesControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Rules);
        return createMailAppFlyoutAnchor(
            getReadRibbonId(MailRibbonControlId.Rules, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            {
                sections: getRulesSections(
                    itemId,
                    targetWindow,
                    useIconColor ? getToolbarRibbonIconColor() : undefined
                ),
            },
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.Rules),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(MailRibbonControlId.Rules, itemId),
                  }
                : undefined
        );
    }
);

export const getReplyControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondReply);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.RespondReply, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onReplyClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.RespondReply)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.RespondReply, itemId) }
                : undefined
        );
    }
);

export const getReplyAllControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondReplyAll);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.RespondReplyAll, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onReplyAllClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.RespondReplyAll)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(
                          MailRibbonControlId.RespondReplyAll,
                          itemId
                      ),
                  }
                : undefined
        );
    }
);

export const getForwardControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.RespondForward);
        return createMailAppSplitButton(
            getReadRibbonId(MailRibbonControlId.RespondForward, itemId),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            {
                sections: [
                    {
                        controls: getForwardSections(itemId, targetWindow),
                    },
                ],
            },
            onForwardClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.RespondForward)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(
                          MailRibbonControlId.RespondForward,
                          itemId
                      ),
                  }
                : undefined
        );
    }
);

export const getReplyWithMeetingControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReplyWithMeeting);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.ReplyWithMeeting, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onReplyWithMeetingClicked(itemId),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.ReplyWithMeeting)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(
                          MailRibbonControlId.ReplyWithMeeting,
                          itemId
                      ),
                  }
                : undefined
        );
    }
);

export const getUnreadReadControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ReadUnread);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.ReadUnread, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onMarkAsReadUnreadClicked(itemId),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.ReadUnread)
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.ReadUnread, itemId) }
                : undefined
        );
    }
);

export const getFlagUnflagControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.FlagUnflag);
        return createMailAppSplitButton(
            getReadRibbonId(MailRibbonControlId.FlagUnflag, itemId),
            controlInfo.text,
            controlInfo.ariaText,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            { sections: getFlagUnflagSections(keytipTabId, itemId) },
            onFlagUnflagClicked(undefined, undefined, itemId),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.FlagUnflag),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.FlagUnflag, itemId) }
                : undefined
        );
    }
);

export const getPrintControl = computedFn(
    (
        isSLR: boolean,
        useIconColor: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.Print);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.Print, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onPrintClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.Print)
            ),
            controlInfo.iconName,
            useIconColor ? getToolbarRibbonIconColor() : undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.Print, itemId) }
                : undefined
        );
    }
);

export const getImmersiveReaderControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined,
        targetWindow: Window | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.ImmersiveReader);
        return createMailAppButton(
            getReadRibbonId(MailRibbonControlId.ImmersiveReader, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            onShowInImmersiveReaderClicked(itemId, targetWindow),
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.ImmersiveReader)
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? {
                      disabled: getIsPopoutButtonDisabled(
                          MailRibbonControlId.ImmersiveReader,
                          itemId
                      ),
                  }
                : undefined
        );
    }
);

export const getAssignPolicyControl = computedFn(
    (
        isSLR: boolean,
        keytipTabId: MailRibbonTabId,
        itemId: ClientItemId | undefined
    ): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.AssignPolicy);
        return createMailAppFlyoutAnchor(
            getReadRibbonId(MailRibbonControlId.AssignPolicy, itemId),
            controlInfo.text,
            controlInfo.tooltip,
            itemId !== undefined /*isPopout*/,
            { sections: getAssignPolicySections(keytipTabId, itemId) },
            constructKeytip(
                [getKeytipString(keytipTabId)],
                getKeytipString(MailRibbonControlId.AssignPolicy),
                true /* hasMenu */
            ),
            controlInfo.iconName,
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            itemId !== undefined
                ? { disabled: getIsPopoutButtonDisabled(MailRibbonControlId.AssignPolicy, itemId) }
                : undefined
        );
    }
);

export const getQuickStepsControl = computedFn(
    (isSLR: boolean): RibbonControlProps => {
        const controlInfo = getQuickSteps()[0];
        return createMailAppSplitButton(
            MailRibbonControlId.QuickStepsGallery,
            controlInfo.name,
            undefined,
            controlInfo.description,
            false /*isPopout*/,
            { sections: getQuickStepsSections() },
            onExecuteQuickStep(controlInfo.id),
            constructKeytip(
                [getKeytipString(MailRibbonTabId.Tab_Home)],
                getKeytipString(MailRibbonControlId.QuickStepsGallery),
                true /* hasMenu */
            ),
            getIconNameFromIconId(controlInfo.iconId),
            undefined /*iconColor*/,
            isSLR ? getDefaultSlrAppRibbonButtonStyles() : undefined,
            {
                primaryDisabled: getIsQuickStepDisabled(controlInfo.actions),
            }
        );
    }
);

export const getPlayMyEmailsControl = computedFn(
    (tabId: MailRibbonTabId): RibbonControlProps => {
        const controlInfo = getControlInfo(MailRibbonControlId.PlayMyEmails);
        return createMailAppButton(
            MailRibbonControlId.PlayMyEmails,
            controlInfo.text,
            controlInfo.tooltip,
            false /*isPopout*/,
            onPMEClicked(tabId),
            constructKeytip(
                [getKeytipString(tabId)],
                getKeytipString(MailRibbonControlId.PlayMyEmails)
            ),
            controlInfo.iconName
        );
    }
);
